import type { ReactNode } from 'react';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { EmptyState } from '@/shared/feedback';
import type { SelectOption } from '@/shared/ui/select';
import type { CategoryProposal } from '@/api/schemas/ai-assistance';
import type { Category } from '@/api/schemas/catalog-taxonomy';
import type {
  CategoryDraft,
  CategoryDraftPatch,
  ManualCategoryInput,
  SaveOutcome,
} from '@/modules/catalog/pages/setup-page/types';
import { CategoryRow } from '@/modules/catalog/pages/setup-page/parts/category-row';
import { ReviewNotices } from '@/modules/catalog/pages/setup-page/parts/review-notices';
import { OutcomeLine } from '@/modules/catalog/pages/setup-page/parts/outcome-line';
import { ManualCategoryForm } from '@/modules/catalog/pages/setup-page/parts/manual-forms';

/**
 * The CATEGORIES half of the page.
 *
 * The order is the merchant's order: describe, ask, REVIEW, accept, save.
 * The proposal is never saved without being shown first, and nothing is saved
 * that the merchant has not accepted.
 */
export type CategoryPanelProps = {
  readonly drafts: ReadonlyArray<CategoryDraft>;
  readonly proposal: CategoryProposal | null;
  readonly existingCategories: ReadonlyArray<Category>;
  readonly parentOptions: SelectOption[];
  readonly canSuggest: boolean;
  readonly isWorking: boolean;
  readonly failure: string | null;
  /** What happened to the category the merchant typed by hand. */
  readonly manualOutcome: SaveOutcome | null;
  readonly onSuggest: () => void;
  readonly onChange: (id: string, patch: CategoryDraftPatch) => void;
  readonly onCreateSelected: () => void;
  readonly onManualCreate: (input: ManualCategoryInput) => void;
};

export function CategoryPanel(props: CategoryPanelProps): ReactNode {
  const accepted = props.drafts.filter((draft) => draft.accepted).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="secondary"
          disabled={!props.canSuggest}
          loading={props.isWorking}
          onClick={props.onSuggest}
        >
          Suggest categories
        </Button>
        <Button
          disabled={accepted === 0 || props.isWorking}
          loading={props.isWorking}
          onClick={props.onCreateSelected}
        >
          Create selected
        </Button>
        {accepted > 0 ? (
          <span className="text-sm text-content-muted">
            {accepted} of {props.drafts.length} accepted
          </span>
        ) : null}
      </div>

      {props.failure === null ? null : (
        <p className="rounded-lg border border-critical bg-critical-soft p-3 text-sm text-content">
          {props.failure}
        </p>
      )}

      {props.proposal === null ? null : <CategoryReview {...props} proposal={props.proposal} />}

      <ManualCategoryForm onCreate={props.onManualCreate} isWorking={props.isWorking} />
      <OutcomeLine outcome={props.manualOutcome} />

      <CategoryList categories={props.existingCategories} />
    </div>
  );
}

/** The proposal, for review: what it reused, what it warned about, what it proposes. */
function CategoryReview({
  drafts,
  proposal,
  parentOptions,
  onChange,
}: Omit<CategoryPanelProps, 'proposal'> & { readonly proposal: CategoryProposal }): ReactNode {
  return (
    <div className="space-y-3">
      <ReviewNotices
        reused={proposal.reused}
        warnings={proposal.warnings}
        missing={proposal.missingRequiredFields}
      />
      {drafts.map((draft) => (
        <CategoryRow
          key={draft.id}
          draft={draft}
          parentOptions={parentOptions}
          onChange={onChange}
        />
      ))}
    </div>
  );
}

/** The categories the tenant already has, so the result of a save is visible. */
function CategoryList({ categories }: { categories: ReadonlyArray<Category> }): ReactNode {
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-medium text-content">Categories you have</h3>
      {categories.length === 0 ? (
        <EmptyState
          title="No categories yet"
          description="Nothing has been created in this catalogue. The first one you create starts the tree."
        />
      ) : (
        <ul className="space-y-1">
          {categories.map((category) => (
            <li key={category.categoryId} className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium text-content">{category.name}</span>
              <span className="text-content-subtle">{category.slug}</span>
              <Badge tone={category.status === 'enabled' ? 'positive' : 'neutral'}>
                {category.status === 'enabled' ? 'Enabled' : 'Disabled'}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
